"use client";

import { useScrollEdges } from "@/hooks/use-scroll-edges";
import { useDragScroll } from "@/hooks/use-drag-scroll";
import Image from "next/image";
import { Link } from "@/i18n/routing";
import { trendingItems as TRENDING } from "@/fixtures/ejinsight/home-fixtures";
import { EJINSIGHT_ARTICLE_ROUTE } from "@/lib/routes";
import { EJINSIGHT_COLORS, EJINSIGHT_TYPE } from "@/lib/theme/ejinsight";
import { Eyebrow } from "../shared/eyebrow";
import { PillTag } from "../shared/pill-tag";
import { ArrowButton } from "../shared/arrow-button";

export function TrendingRibbon() {
  // Arrows disable at the ends instead of staying live and no-oping.
  const { ref: scrollerRef, atStart, atEnd } = useScrollEdges<HTMLDivElement>();

  /*
    Dragging with a cursor settles on a card, exactly as the arrows do.

    The strip scrolled freely under a mouse drag and stopped wherever it was
    let go, mid-card — QA: "add the same snapping effect to the carousel slider
    when swiped by cursor". The shared hook is the one the LJ, EJ Tech and EJ
    Health carousels use: it settles on the nearest card on release and
    swallows the click that would otherwise follow a drag, so dragging across a
    card does not open it. Touch is untouched — the hook ignores touch pointers
    and CSS `snap-mandatory` already handles a finger.
  */
  const drag = useDragScroll<HTMLDivElement>(scrollerRef, {
    snapToChildren: true,
    childSelector: "[data-trending-card]",
  });

  /**
   * Advance by a GROUP, not by a card.
   *
   * The arrows moved one card per press, so clearing a row of four took four
   * presses and the strip crept rather than paged. QA: "slide by group". A
   * step of `clientWidth` rounded DOWN to a whole number of cards lands the
   * next group flush at the left edge — with `snap-mandatory` on the row, the
   * browser then settles it exactly on that card's start.
   */
  const scrollBy = (dir: "prev" | "next") => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-trending-card]");
    const cardStep = card ? card.offsetWidth + 24 : 280;
    const perView = Math.max(1, Math.floor(el.clientWidth / cardStep));
    const step = cardStep * perView;
    el.scrollBy({ left: dir === "next" ? step : -step, behavior: "smooth" });
  };

  return (
    <section className="w-full">
      <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div className="flex flex-col gap-3">
            <Eyebrow tone="accent" withDash>
              Trending
            </Eyebrow>
            <h2
              className="m-0"
              style={{
                ...EJINSIGHT_TYPE.h3,
                fontSize: "clamp(22px, 3vw, 28px)",
                color: EJINSIGHT_COLORS.ink,
              }}
            >
              The stories Hong Kong is reading now
            </h2>
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            <ArrowButton
              direction="prev"
              disabled={atStart}
              onClick={() => scrollBy("prev")}
              ariaLabel="Scroll trending left"
            />
            <ArrowButton
              direction="next"
              disabled={atEnd}
              onClick={() => scrollBy("next")}
              ariaLabel="Scroll trending right"
            />
          </div>
        </div>

        {/* The strip stays INSIDE the section container. It used to bleed to
            the viewport edges (`-mx-4 sm:-mx-6 lg:-mx-8`), so it read as wider
            than every other section on the page and its first card sat left of
            the heading above it. QA: "make the container width of the carousel
            the same as other sections". */}
        <div
          ref={scrollerRef}
          {...drag.dragProps}
          className={`overflow-x-auto pb-2 ${drag.dragClass}`}
          style={{ scrollbarWidth: "none" }}
        >
          {/* NOT `w-max`.

              The cards size themselves as a percentage on the tablet band, and
              a percentage resolves against this row — which with `w-max` is as
              wide as its own content (2522px at 820), so `(100%-48px)/2.15`
              came out at 1150px per card. Without it the row is the scroller's
              width and the percentage means what it says; the cards are
              `shrink-0`, so they still overflow and the row still scrolls. */}
          <div className="flex snap-x snap-mandatory gap-6">
            {TRENDING.map((row, i) => (
            <Link
              key={row.title}
              // Was the home page — the card you clicked reloaded the page you
              // were on. QA: "click on the article item should redirect to
              // article details page".
              href={EJINSIGHT_ARTICLE_ROUTE}
              data-trending-card
              // 2.15 cards in view on the TABLET band, so the third is visibly
              // cut and the strip reads as a carousel rather than a row that
              // happens to end — QA: "set the carousel to 2.15 items per
              // slide". The 48px is the two gaps that sit inside the viewport
              // at that count. Phone and desktop keep their fixed widths.
              className="group hkej-tap relative flex w-[300px] shrink-0 snap-start flex-col gap-4 sm:w-[340px] md:w-[calc((100%-48px)/2.15)] lg:w-[340px]"
            >
              <span
                className="relative aspect-[16/9] w-full overflow-hidden"
                style={{ backgroundColor: EJINSIGHT_COLORS.surfaceAlt }}
              >
                <Image
                  src={row.img}
                  alt={row.title}
                  fill
                  sizes="340px"
                  className="object-contain object-center transition-transform duration-500 group-hover:scale-[1.05]"
                  unoptimized
                />
                <span
                  className="absolute bottom-4 left-4 inline-flex h-14 min-w-[64px] items-center justify-center rounded-2xl px-3"
                  style={{
                    backgroundColor: EJINSIGHT_COLORS.primary,
                    color: EJINSIGHT_COLORS.textOnDark,
                  }}
                >
                  <span
                    style={{
                      fontFamily: EJINSIGHT_TYPE.hero.fontFamily,
                      fontWeight: 700,
                      fontStyle: "italic",
                      fontSize: 28,
                      letterSpacing: "-0.02em",
                      lineHeight: 1,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </span>
              </span>
              <div className="flex flex-col gap-2">
                <PillTag category={row.category} variant="soft">
                  {row.category}
                </PillTag>
                <h3
                  className="hkej-tap-title m-0 line-clamp-3"
                  style={{
                    ...EJINSIGHT_TYPE.cardTitle,
                    color: EJINSIGHT_COLORS.ink,
                  }}
                >
                  {row.title}
                </h3>
                <span
                  style={{
                    ...EJINSIGHT_TYPE.meta,
                    color: EJINSIGHT_COLORS.muted,
                  }}
                >
                  {row.date}
                </span>
              </div>
            </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
